import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Md5BypassService } from '../../services/md5-bypass.service';

@Component({
  selector: 'app-dev-bypass-helper',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dev-helper-panel" *ngIf="isVisible">
      <h3 *ngIf="!environment.production">⚙️ Bypass DevTools</h3>

      <div class="info-section">
        <!-- Hash actual -->
        <div class="hash-section">
          <p><strong>Hash<span *ngIf="!environment.production"> MD5</span></strong></p>
          <code class="hash-code" (click)="copyHash()">{{ bypassInfo?.hash }}</code>
          <button (click)="copyHash()" class="copy-btn-mini">📋</button>
        </div>

        <!-- Success Message -->

        <div class="alert alert-success text-center" role="alert" *ngIf="isAlertVisible">
            ✅ ¡Copiado satisfactoriamente en el  portapapeles!
        </div>

        <!-- Countdown timer -->
        <div class="timer-section" *ngIf="!environment.production && countdown">
          <p class="timer-text">
            ⏱️ <strong>Expira en:</strong>
            <span class="countdown" [class.warning]="countdown.totalSeconds < 300">
              {{ countdown.minutes }}m {{ countdown.seconds }}s
            </span>
          </p>
        </div>

        <!-- Información adicional para desarrollo -->
        <div *ngIf="!environment.production">
          <p><strong>Parámetro:</strong> <code>{{ bypassInfo?.paramName }}</code></p>
          <p><strong>Válido hasta:</strong> {{ bypassInfo?.validUntil }}</p>
        </div>

        <!-- URL con bypass actual -->
        <div class="url-section" *ngIf="!environment.production">
          <label for="bypass-url"><strong>URL actual:</strong></label>
          <input
            id="bypass-url"
            type="text"
            readonly
            [value]="bypassInfo?.exampleUrl"
            class="url-input"
            (click)="copyUrl($event)">
          <button (click)="copyUrl()" class="copy-btn">📋 Copiar URL</button>
        </div>

        <!-- Próximo hash (solo en desarrollo) -->
        <div class="next-hash-section" *ngIf="!environment.production && bypassInfo?.nextHash">
          <details>
            <summary><strong>🔮 Próximo Hash ({{ bypassInfo?.nextValidFrom }})</strong></summary>
            <div class="next-hash-content">
              <code class="hash-code next-hash" (click)="copyNextHash()">{{ bypassInfo?.nextHash }}</code>
              <button (click)="copyNextHash()" class="copy-btn-mini">📋</button>
              <p class="next-hash-info">Este hash será válido desde las {{ bypassInfo?.nextValidFrom }}</p>
            </div>
            <!-- Success Message -->

            <div class="alert alert-success text-center" role="alert" *ngIf="isNextHashAlertVisible">
                ✅ ¡Hash copiado al portapapeles!
            </div>
          </details>
        </div>

        <!-- URLs extendidas (solo en desarrollo) -->
        <div class="extended-urls" *ngIf="!environment.production && showExtendedUrls">
          <details>
            <summary><strong>🔗 URLs para múltiples horas</strong></summary>
            <div class="extended-content">
              <div class="url-item" *ngFor="let urlInfo of extendedUrls">
                <p><strong>{{ urlInfo.label }}:</strong></p>
                <input
                  type="text"
                  readonly
                  [value]="urlInfo.url"
                  class="url-input-small"
                  (click)="copySpecificUrl($event, urlInfo.url)">
                <button (click)="copySpecificUrl(null, urlInfo.url)" class="copy-btn-mini">📋</button>
              </div>
            </div>
          </details>
        </div>

        <!-- Instrucciones -->
        <div class="instructions" *ngIf="!environment.production">
          <h4>📋 Instrucciones:</h4>
          <ol>
            <li>Copia la URL actual de arriba</li>
            <li>Pégala en el navegador en producción</li>
            <li>Las DevTools estarán habilitadas por 1-2 horas</li>
            <li><strong>Nota:</strong> Los hashes cambian cada hora automáticamente</li>
          </ol>
        </div>

        <!-- Controles -->
        <div class="controls">
          <button (click)="refreshInfo()" class="refresh-btn" *ngIf="!environment.production">
            🔄 Actualizar
          </button>
          <button (click)="toggleExtendedUrls()" class="toggle-btn" *ngIf="!environment.production">
            {{ showExtendedUrls ? '🔼 Ocultar URLs' : '🔽 Mostrar URLs' }}
          </button>
          <button (click)="toggleVisible()" class="copy-btn">❌ Cerrar</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .dev-helper-panel {
      position: fixed;
      bottom: 10px;
      right: 18px;
      background: #f8f9fa;
      border: 2px solid #007bff;
      border-radius: 8px;
      padding: 15px;
      max-width: 450px;
      font-family: Arial, sans-serif;
      font-size: 12px;
      z-index: 9999;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .dev-helper-panel h3 {
      margin: 0 0 15px 0;
      color: #007bff;
      font-size: 14px;
      font-weight: bold;
    }

    .hash-section {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0;
    }

    .hash-section p {
      margin: 0;
      flex-shrink: 0;
    }

    .hash-code {
      background: #e9ecef;
      padding: 4px 6px;
      border-radius: 3px;
      font-family: monospace;
      font-size: 11px;
      cursor: pointer;
      flex: 1;
      border: 1px solid transparent;
      transition: all 0.2s;
    }

    .hash-code:hover {
      background: #dee2e6;
      border-color: #007bff;
    }

    .timer-section {
      background: #f0f8ff;
      border: 1px solid #b8daff;
      border-radius: 4px;
      padding: 8px;
      margin: 10px 0;
      text-align: center;
    }

    .timer-text {
      margin: 0;
      font-size: 11px;
    }

    .countdown {
      font-family: monospace;
      font-weight: bold;
      color: #28a745;
    }

    .countdown.warning {
      color: #dc3545;
      animation: blink 1s infinite;
    }

    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0.5; }
    }

    .info-section p {
      margin: 8px 0;
    }

    code {
      background: #e9ecef;
      padding: 2px 4px;
      border-radius: 3px;
      font-family: monospace;
      font-size: 11px;
    }

    .url-section {
      margin: 15px 0;
    }

    .url-input, .url-input-small {
      width: 100%;
      padding: 5px;
      margin: 5px 0;
      border: 1px solid #ccc;
      border-radius: 3px;
      font-size: 10px;
      font-family: monospace;
    }

    .url-input-small {
      font-size: 9px;
    }

    .copy-btn, .copy-btn-mini, .refresh-btn, .toggle-btn {
      background: #007bff;
      color: white;
      border: none;
      padding: 5px 10px;
      border-radius: 3px;
      cursor: pointer;
      font-size: 10px;
      margin: 2px;
    }

    .copy-btn-mini {
      padding: 2px 6px;
      font-size: 9px;
    }

    .refresh-btn {
      background: #28a745;
    }

    .toggle-btn {
      background: #6c757d;
    }

    .copy-btn:hover, .copy-btn-mini:hover {
      background: #0056b3;
    }

    .refresh-btn:hover {
      background: #218838;
    }

    .toggle-btn:hover {
      background: #545b62;
    }

    .next-hash-section {
      margin: 15px 0;
      border: 1px solid #ffc107;
      border-radius: 4px;
      background: #fff3cd;
    }

    .next-hash-section summary {
      padding: 8px;
      cursor: pointer;
      font-size: 11px;
    }

    .next-hash-content {
      padding: 8px;
      border-top: 1px solid #ffc107;
    }

    .next-hash {
      background: #fff3cd;
      border: 1px solid #ffc107;
    }

    .next-hash-info {
      font-size: 10px;
      color: #856404;
      margin: 5px 0 0 0;
    }

    .extended-urls {
      margin: 15px 0;
      border: 1px solid #17a2b8;
      border-radius: 4px;
      background: #d1ecf1;
    }

    .extended-urls summary {
      padding: 8px;
      cursor: pointer;
      font-size: 11px;
    }

    .extended-content {
      padding: 8px;
      border-top: 1px solid #17a2b8;
    }

    .url-item {
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .url-item p {
      margin: 0;
      font-size: 10px;
      min-width: 80px;
    }

    .instructions {
      margin-top: 15px;
      padding-top: 15px;
      border-top: 1px solid #ccc;
    }

    .instructions h4 {
      margin: 0 0 8px 0;
      font-size: 12px;
      font-weight: bold;
    }

    .instructions ol {
      margin: 0;
      padding-left: 15px;
    }

    .instructions li {
      margin: 3px 0;
      font-size: 11px;
    }

    .controls {
      border-top: 1px solid #ccc;
      padding-top: 15px;
      text-align: right;
      display: flex;
      gap: 5px;
      justify-content: flex-end;
      flex-wrap: wrap;
    }

    details {
      margin: 5px 0;
    }

    details[open] summary {
      border-bottom: 1px solid currentColor;
    }
  `]
})
export class DevBypassHelperComponent implements OnInit, OnDestroy {
  private md5BypassService = inject(Md5BypassService);
  private route = inject(ActivatedRoute);

  environment = environment;
  bypassInfo: any = null;
  isVisible = false;
  showExtendedUrls = false;
  countdown: { minutes: number; seconds: number; totalSeconds: number } | null = null;
  extendedUrls: Array<{ label: string; url: string }> = [];
  isAlertVisible = false;
  isNextHashAlertVisible = false;

  private countdownInterval: any;

  ngOnInit(): void {
    this.checkBypassParameter();
    this.updateBypassInfo();
    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private checkBypassParameter(): void {
    this.route.queryParams.subscribe(params => {
      const paramValue = params[environment.md5Config.devToolsSecretParam];

      if (paramValue === environment.md5Config.secretPhrase) {
        this.isVisible = true;
        console.log('DevBypass Helper activado');
      } else {
        this.isVisible = false;
      }
    });
  }

  private updateBypassInfo(): void {
    this.bypassInfo = this.md5BypassService.getBypassInfo();

    if (!environment.production) {
      const extended = this.md5BypassService.generateExtendedBypassUrls();
      this.extendedUrls = [
        { label: '⏮️ Anterior', url: extended.previous.url },
        { label: '⏯️ Actual', url: extended.current.url },
        { label: '⏭️ Próximo', url: extended.next.url }
      ];
    }
  }

  private startCountdown(): void {
    if (environment.production) return;

    this.updateCountdown();
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  private updateCountdown(): void {
    this.countdown = this.md5BypassService.getTimeUntilNextHash();

    // Actualizar información cuando el hash cambie
    if (this.countdown.totalSeconds === 0) {
      setTimeout(() => {
        this.updateBypassInfo();
      }, 1000);
    }
  }

  toggleVisible(): void {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.updateBypassInfo();
    }
  }

  toggleExtendedUrls(): void {
    this.showExtendedUrls = !this.showExtendedUrls;
  }

  refreshInfo(): void {
    this.updateBypassInfo();
    console.log('📱 Información de bypass actualizada');
  }

  copyHash(): void {
    if (this.bypassInfo?.hash) {
      navigator.clipboard.writeText(this.bypassInfo.hash).then(() => {
        this.isAlertVisible = !this.isAlertVisible;
        console.log('🔑 Hash copiado al portapapeles');
      }).catch(err => {
        console.error('Error al copiar hash:', err);
      });

      setTimeout(() => {
            this.isAlertVisible = !this.isAlertVisible;
      }, 1200);
    }
  }

  copyNextHash(): void {
    if (this.bypassInfo?.nextHash) {
      navigator.clipboard.writeText(this.bypassInfo.nextHash).then(() => {
        this.isNextHashAlertVisible = !this.isNextHashAlertVisible;
        console.log('🔮 Próximo hash copiado al portapapeles');
      }).catch(err => {
        console.error('Error al copiar próximo hash:', err);
      });
    }

    setTimeout(() => {
            this.isNextHashAlertVisible = !this.isNextHashAlertVisible;
      }, 1200);
  }

  copyUrl(event?: Event): void {
    if (event && event.target) {
      (event.target as HTMLInputElement).select();
    }

    if (this.bypassInfo?.exampleUrl) {
      navigator.clipboard.writeText(this.bypassInfo.exampleUrl).then(() => {
        this.isAlertVisible = !this.isAlertVisible
        
        console.log('🔗 URL copiada al portapapeles');
      }).catch(err => {
        console.error('Error al copiar URL:', err);
      });
      setTimeout(() => {
            this.isAlertVisible = !this.isAlertVisible
        }, 1200);
    }
  }

  copySpecificUrl(event: Event | null, url: string): void {
    if (event && event.target) {
      (event.target as HTMLInputElement).select();
    }

    navigator.clipboard.writeText(url).then(() => {
      console.log('🔗 URL específica copiada al portapapeles');
    }).catch(err => {
      console.error('Error al copiar URL específica:', err);
    });
  }
}
